import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogTitle,
  Badge,
  Button,
  LoadingSpinner,
} from "@/components/ui";
import { searchContactsQueryOptions } from "@/lib/data/react-query/contacts/contact";
import {
  type DuplicateMatchReason,
  type PossibleDuplicateContact,
} from "@/lib/data/requests";
import { type Contact } from "@/lib/types";
import { cn } from "@/lib/utils/cn";
import {
  formatAddress,
  formatContactsName,
  formatLocalOrIntlPhoneNumber,
} from "@/lib/utils/format";
import { useQuery } from "@tanstack/react-query";
import { Info } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

type DuplicateContactSelectionModalProps = {
  contacts: PossibleDuplicateContact[];
  open: boolean;
  onCreateAsNew: () => void | Promise<void>;
  onGoBack: () => void;
  onUseContact: (contactId: string) => void | Promise<void>;
};

const MATCH_REASON_LABELS: Record<DuplicateMatchReason, string> = {
  exactName: "Name",
  similarName: "Similar name",
  email: "Email",
  phone: "Phone",
  address: "Address",
};

const MATCH_REASON_WEIGHTS: Record<DuplicateMatchReason, number> = {
  exactName: 4,
  email: 3,
  phone: 3,
  address: 2,
  similarName: 1,
};

const matchStrength = (contact: PossibleDuplicateContact) =>
  contact.matchedOn.reduce(
    (total, reason) => total + MATCH_REASON_WEIGHTS[reason],
    0,
  );

const getMatchReasonLabel = (contact: PossibleDuplicateContact) =>
  [...new Set(contact.matchedOn)]
    .sort((a, b) => MATCH_REASON_WEIGHTS[b] - MATCH_REASON_WEIGHTS[a])
    .map((reason) => MATCH_REASON_LABELS[reason])
    .join(" + ");

const getPrimaryOrFirst = <T extends { isPrimary?: boolean | null }>(
  values?: T[],
) => values?.find((value) => value.isPrimary) ?? values?.[0];

export const DuplicateContactSelectionModal = ({
  contacts,
  open,
  onCreateAsNew,
  onGoBack,
  onUseContact,
}: DuplicateContactSelectionModalProps) => {
  const contactIds = useMemo(() => contacts.map(({ id }) => id), [contacts]);
  const needsContactDetails = contacts.some(
    (contact) => !contact.phones || !contact.addresses,
  );
  const contactIdsKey = contactIds.join(",");
  const sortedContacts = useMemo(
    () =>
      contacts
        .map((contact, index) => ({ contact, index }))
        .sort(
          (a, b) =>
            matchStrength(b.contact) - matchStrength(a.contact) ||
            a.index - b.index,
        )
        .map(({ contact }) => contact),
    [contacts],
  );
  const [selectedContactId, setSelectedContactId] = useState<string | null>(
    null,
  );
  const [pendingAction, setPendingAction] = useState<"create" | "use" | null>(
    null,
  );

  const detailsQuery = useQuery({
    ...searchContactsQueryOptions({ contactIds }),
    enabled: open && contactIds.length > 0 && needsContactDetails,
  });
  const contactDetailsById = useMemo(
    () =>
      new Map(
        (detailsQuery.data?.data ?? []).map((contact) => [contact.id, contact]),
      ),
    [detailsQuery.data?.data],
  );

  useEffect(() => {
    if (!open) {
      setSelectedContactId(null);
      setPendingAction(null);
      return;
    }
    setSelectedContactId(contacts.length === 1 ? contacts[0].id : null);
  }, [contactIdsKey, contacts, open]);

  const runAction = async (
    action: "create" | "use",
    callback: () => void | Promise<void>,
  ) => {
    setPendingAction(action);
    try {
      await callback();
    } finally {
      setPendingAction(null);
    }
  };

  return (
    <AlertDialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen && !pendingAction) {
          onGoBack();
        }
      }}
    >
      <AlertDialogContent className="max-w-3xl gap-0 overflow-hidden p-0">
        <div className="p-6 pb-5">
          <AlertDialogTitle>Possible duplicate contact</AlertDialogTitle>
          <AlertDialogDescription className="mt-2">
            Choose an existing contact, or continue with creating a new one.
          </AlertDialogDescription>

          <div
            aria-label="Matching contacts"
            className="border-border mt-5 h-[196px] overflow-y-auto overscroll-contain rounded-lg border"
            role="radiogroup"
          >
            {sortedContacts.map((duplicateContact) => {
              const details = contactDetailsById.get(duplicateContact.id);
              const displayContact: Partial<Contact> =
                details ?? duplicateContact;
              const phone = getPrimaryOrFirst(
                details?.phones ?? duplicateContact.phones,
              );
              const address = getPrimaryOrFirst(
                details?.addresses ?? duplicateContact.addresses,
              );
              const selected = selectedContactId === duplicateContact.id;

              return (
                <button
                  aria-checked={selected}
                  className={cn(
                    "border-border hover:bg-muted/50 focus-visible:ring-ring grid min-h-[58px] w-full grid-cols-[20px_minmax(0,1fr)_auto] items-center gap-3 border-b px-4 py-3 text-left last:border-b-0 focus-visible:ring-2 focus-visible:outline-none focus-visible:ring-inset",
                    selected && "bg-primary/5",
                  )}
                  key={duplicateContact.id}
                  onClick={() => {
                    setSelectedContactId(duplicateContact.id);
                  }}
                  role="radio"
                  type="button"
                >
                  <span
                    aria-hidden
                    className="border-input flex h-4 w-4 items-center justify-center rounded-full border"
                  >
                    {selected ? (
                      <span className="bg-primary h-2 w-2 rounded-full" />
                    ) : null}
                  </span>
                  <span className="grid min-w-0 grid-cols-[minmax(0,0.75fr)_minmax(0,0.8fr)_minmax(0,1.4fr)] items-center gap-3 text-sm">
                    <span className="truncate font-medium">
                      {formatContactsName(displayContact) ?? "Contact"}
                    </span>
                    <span className="text-muted-foreground truncate">
                      {phone
                        ? formatLocalOrIntlPhoneNumber(phone) || phone.number
                        : "—"}
                    </span>
                    <span className="text-muted-foreground truncate">
                      {address ? formatAddress(address) || "—" : "—"}
                    </span>
                  </span>
                  <Badge
                    className="shrink-0 whitespace-nowrap"
                    variant="outline"
                  >
                    {getMatchReasonLabel(duplicateContact)}
                  </Badge>
                </button>
              );
            })}
          </div>

          <p className="text-muted-foreground mt-4 flex items-center gap-2 text-sm">
            <Info aria-hidden className="h-4 w-4 shrink-0" />
            Using an existing contact discards the details you typed.
          </p>
        </div>

        <div className="border-border flex items-center justify-between gap-3 border-t px-6 py-4">
          <Button
            disabled={pendingAction !== null}
            onClick={onGoBack}
            type="button"
            variant="ghost"
          >
            Go back
          </Button>
          <div className="flex items-center gap-2">
            <Button
              disabled={pendingAction !== null}
              onClick={() => runAction("create", onCreateAsNew)}
              type="button"
              variant="secondary"
            >
              {pendingAction === "create" ? <LoadingSpinner /> : null}
              Create as new
            </Button>
            <Button
              disabled={!selectedContactId || pendingAction !== null}
              onClick={() => {
                if (selectedContactId) {
                  void runAction("use", () => onUseContact(selectedContactId));
                }
              }}
              type="button"
            >
              {pendingAction === "use" ? <LoadingSpinner /> : null}
              {contacts.length === 1
                ? "Use this contact"
                : "Use selected contact"}
            </Button>
          </div>
        </div>
      </AlertDialogContent>
    </AlertDialog>
  );
};
